import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import complianceReportsCustomizationService from '../../../services/complianceReportsCustomizationService';

const TemplateLibrary = ({ onApplyTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await complianceReportsCustomizationService?.getAllTemplates();
      setTemplates(data);
    } catch (err) {
      setError(err?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const presetTemplates = [
    {
      id: 'quarterly-board',
      name: 'Quarterly Board Report',
      description: 'Executive summary for board meetings with key metrics and trends',
      icon: 'Briefcase',
      color: 'primary'
    },
    {
      id: 'regulatory-submission',
      name: 'Regulatory Submission',
      description: 'DLIR-ready compliance report with all required documentation',
      icon: 'FileCheck',
      color: 'success'
    },
    {
      id: 'departmental-assessment',
      name: 'Departmental Assessment',
      description: 'Detailed department-level compliance analysis',
      icon: 'Building2',
      color: 'warning'
    }
  ];

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader2" size={32} color="var(--color-primary)" className="animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="BookTemplate" size={20} />
          Template Library
        </h3>
        <Button variant="outline" size="sm" iconName="Plus">
          Create Template
        </Button>
      </div>

      {error && (
        <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md flex items-start gap-2 mb-4">
          <Icon name="AlertCircle" size={18} className="mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Preset Templates</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {presetTemplates?.map((template) => (
              <div
                key={template?.id}
                className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => onApplyTemplate({
                  includeViolationTrends: true,
                  includeDepartmentMetrics: true,
                  includeCostAnalysis: template?.id === 'quarterly-board',
                  includeDlirReadiness: template?.id === 'regulatory-submission'
                })}
              >
                <div className="flex items-start gap-3">
                  <div className={`bg-${template?.color}/10 p-2 rounded-lg`}>
                    <Icon name={template?.icon} size={20} color={`var(--color-${template?.color})`} />
                  </div>
                  <div className="flex-1">
                    <h5 className="text-sm font-semibold text-foreground mb-1">
                      {template?.name}
                    </h5>
                    <p className="text-xs text-muted-foreground mb-2">
                      {template?.description}
                    </p>
                    <Button variant="ghost" size="xs" iconName="ArrowRight">
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {templates?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Saved Templates</h4>
            <div className="space-y-2">
              {templates?.map((template) => (
                <div
                  key={template?.id}
                  className="bg-muted/30 rounded-lg p-3 hover:bg-muted/50 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <Icon name="FileText" size={18} color="var(--color-primary)" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{template?.templateName}</p>
                      <p className="text-xs text-muted-foreground">{template?.reportType}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Check"
                      onClick={() => onApplyTemplate(template)}
                    >
                      Apply
                    </Button>
                    <Button variant="ghost" size="icon">
                      <Icon name="Edit" size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateLibrary;